import { ChevronLeft, ChevronRight, Loader2, Scissors, Trash2 } from "lucide-react"
import { memo, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import { formatMs } from "@/lib/format-ms"
import type { PlaybackTimeStore } from "@/lib/timeline"
import { sampleFilmstripIndices, type FilmstripSegment } from "@/lib/timeline/plan-filmstrip-cells"
import { drawVideoToCanvas, requestVideoFrameAt } from "@/lib/video-timeline"
import { cn } from "@/lib/utils"

export function TimelineCursor({
  percent,
  labelMs,
  variant,
  interactive,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: {
  percent: number
  labelMs: number
  variant: "playhead" | "marker"
  interactive: boolean
  onPointerDown?: (e: React.PointerEvent) => void
  onPointerMove?: (e: React.PointerEvent) => void
  onPointerUp?: (e: React.PointerEvent) => void
}) {
  const lineClass =
    variant === "playhead"
      ? "bg-ui-accent shadow-sm shadow-violet-400/50"
      : "bg-amber-500 shadow-sm shadow-amber-900/50"
  return (
    <div
      className={cn(
        "absolute inset-y-0 w-3 -translate-x-1/2",
        variant === "marker" ? "z-[62]" : "z-[63]",
        interactive ? "cursor-ew-resize" : "pointer-events-none",
      )}
      style={{ left: `${percent}%` }}
      onPointerDown={interactive ? onPointerDown : undefined}
      onPointerMove={interactive ? onPointerMove : undefined}
      onPointerUp={interactive ? onPointerUp : undefined}
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className={cn("absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2", lineClass)} />
        <span
          className={cn(
            "relative z-10 rounded bg-[var(--ui-chip-bg-strong)] px-1 py-0.5 text-[9px] whitespace-nowrap tabular-nums shadow",
            variant === "playhead" ? "text-ui-text-primary" : "text-amber-100",
          )}
        >
          {formatMs(labelMs)}
        </span>
      </div>
    </div>
  )
}

/** Пауза после последнего изменения позиции, прежде чем дёргать декодер. */
const MARKER_PREVIEW_DEBOUNCE_MS = 80

/**
 * Два кадра вокруг позиции (n-1 и n) на скрытом <video>.
 *
 * `msStore` — для живой позиции (скраб плейхеда): подписка вместо пропа, чтобы
 * перетаскивание не рождало React-рендеров. `ms` — для статичной позиции (ховер
 * по маркеру). Сики дебаунсятся и коалесятся: без этого каждый пиксель драга
 * ставил два сика в очередь к одному и тому же элементу.
 */
export function MarkerFramePreview({
  ms,
  msStore,
  frameMs,
  videoRef,
  aspectRatio,
}: {
  ms?: number
  msStore?: PlaybackTimeStore
  frameMs: number
  videoRef: React.RefObject<HTMLVideoElement | null>
  aspectRatio: number
}) {
  const leftRef = useRef<HTMLCanvasElement>(null)
  const rightRef = useRef<HTMLCanvasElement>(null)
  const leftLabelRef = useRef<HTMLSpanElement>(null)
  const rightLabelRef = useRef<HTMLSpanElement>(null)
  const canvasW = 64
  const canvasH = Math.max(24, Math.round(64 / (aspectRatio > 0 ? aspectRatio : 16 / 9)))

  useEffect(() => {
    const leftCanvas = leftRef.current
    const rightCanvas = rightRef.current
    const video = videoRef.current
    if (!video?.src || !leftCanvas || !rightCanvas) return

    leftCanvas.width = canvasW
    leftCanvas.height = canvasH
    rightCanvas.width = canvasW
    rightCanvas.height = canvasH

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let generation = 0

    const currentMs = () => msStore?.get() ?? ms ?? 0

    const run = async () => {
      // Коалесинг в requestVideoFrameAt может увести элемент дальше нашей цели,
      // пока мы ждём. Поколение отсекает устаревший вызов: иначе он нарисовал бы
      // чужой кадр под своей подписью.
      const gen = ++generation
      const tRightMs = currentMs()
      const tLeftMs = Math.max(0, tRightMs - frameMs)
      if (leftLabelRef.current) leftLabelRef.current.textContent = formatMs(tLeftMs)
      if (rightLabelRef.current) rightLabelRef.current.textContent = formatMs(tRightMs)

      if (await requestVideoFrameAt(video, tLeftMs / 1000)) {
        if (cancelled || gen !== generation) return
        drawVideoToCanvas(video, leftCanvas)
      }
      if (cancelled || gen !== generation) return
      if (await requestVideoFrameAt(video, tRightMs / 1000)) {
        if (cancelled || gen !== generation) return
        drawVideoToCanvas(video, rightCanvas)
      }
    }

    const schedule = () => {
      if (timer != null) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        if (!cancelled) void run()
      }, MARKER_PREVIEW_DEBOUNCE_MS)
    }

    const start = () => {
      void run()
      return msStore?.subscribe(schedule)
    }

    let unsubscribe: (() => void) | undefined
    if (video.readyState >= 1) {
      unsubscribe = start()
    } else {
      const onMeta = () => {
        video.removeEventListener("loadedmetadata", onMeta)
        if (!cancelled) unsubscribe = start()
      }
      video.addEventListener("loadedmetadata", onMeta)
      return () => {
        cancelled = true
        if (timer != null) clearTimeout(timer)
        video.removeEventListener("loadedmetadata", onMeta)
        unsubscribe?.()
      }
    }

    return () => {
      cancelled = true
      if (timer != null) clearTimeout(timer)
      unsubscribe?.()
    }
  }, [ms, msStore, frameMs, videoRef, canvasW, canvasH])

  const initialMs = msStore?.get() ?? ms ?? 0

  return (
    <div className="flex shrink-0 items-stretch gap-1 rounded border border-ui-border p-1 shadow-lg dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95">
      <div className="flex flex-col items-center gap-0.5">
        <canvas
          ref={leftRef}
          className="rounded border border-white/5 bg-black"
          style={{ width: canvasW, height: canvasH }}
          width={canvasW}
          height={canvasH}
        />
        <span ref={leftLabelRef} className="text-[7px] text-ui-text-muted tabular-nums">
          {formatMs(Math.max(0, initialMs - frameMs))}
        </span>
      </div>
      <div className="flex flex-col items-center gap-0.5">
        <canvas
          ref={rightRef}
          className="rounded border border-white/5 bg-black"
          style={{ width: canvasW, height: canvasH }}
          width={canvasW}
          height={canvasH}
        />
        <span ref={rightLabelRef} className="text-[7px] text-ui-text-muted tabular-nums">
          {formatMs(initialMs)}
        </span>
      </div>
    </div>
  )
}

export const TIMELINE_FILMSTRIP_INNER_H = 50

/** Subsample frame-boundary lines if the clip is long (keeps DOM light). */
const MAX_FRAME_TICK_LINES = 400

function frameTickPositionsPercent(totalFrames: number): number[] {
  if (totalFrames <= 1) return []
  const internal = totalFrames - 1
  const step = internal <= MAX_FRAME_TICK_LINES ? 1 : Math.ceil(internal / MAX_FRAME_TICK_LINES)
  const positions: number[] = []
  const seen = new Set<number>()
  for (let frameIdx = step; frameIdx < totalFrames; frameIdx += step) {
    const pct = (frameIdx / totalFrames) * 100
    const key = Math.round(pct * 1000)
    if (!seen.has(key)) {
      seen.add(key)
      positions.push(pct)
    }
  }
  return positions.sort((a, b) => a - b)
}

export type TimelineFilmstripProps = {
  frameUrls: string[]
  aspectRatio: number
  rangeStartMs: number
  rangeEndMs: number
  innerHeight?: number
  dimmed?: boolean
  posterUrl?: string | null
  posterStatus?: string
  overlayTopLeft?: ReactNode
  overlayTopRight?: ReactNode
  /**
   * `filmstrip` — миниатюры кадров (аниматик и т.д.).
   * `frameTicks` — только вертикальные деления по длительности/шагу кадра, без картинок (студия).
   */
  variant?: "filmstrip" | "frameTicks"
  /** Мс на кадр; для `frameTicks` задаёт плотность сетки (по умолчанию 40). */
  frameTickMs?: number
  /**
   * Интервал между кадрами filmstrip в мс (по умолчанию 1000 — 1 кадр/сек).
   * Для длинных видео бэкенд снижает fps (кап на количество кадров) — тогда
   * интервал = duration_ms / frameUrls.length.
   */
  frameIntervalMs?: number
  /** Разбиение ленты по файлам: у каждого свой интервал между кадрами. */
  filmstripSegments?: FilmstripSegment[]
  /**
   * Известная ширина в px. Если передана, ResizeObserver не создаётся: у аниматика
   * ширина сегмента выводится из pxPerMs, и N наблюдателей с собственным setState
   * на каждый зум — чистые лишние рендеры.
   */
  widthPx?: number
}

/**
 * Filmstrip for a time range: samples frame URLs across [rangeStartMs, rangeEndMs].
 * Thumbs share the strip width (flex) so there are no black gaps; count comes from
 * how many natural-aspect tiles fit. Narrow strip: poster if ready, else one full-bleed frame.
 */
export function TimelineFilmstrip({
  frameUrls,
  aspectRatio,
  rangeStartMs,
  rangeEndMs,
  innerHeight = TIMELINE_FILMSTRIP_INNER_H,
  dimmed = false,
  posterUrl,
  posterStatus,
  overlayTopLeft,
  overlayTopRight,
  variant = "filmstrip",
  frameTickMs: frameTickMsProp,
  frameIntervalMs,
  filmstripSegments,
  widthPx,
}: TimelineFilmstripProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [observedWidth, setObservedWidth] = useState(0)
  const measure = widthPx == null

  useEffect(() => {
    if (!measure) return
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setObservedWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure])

  const containerWidth = widthPx ?? observedWidth

  if (variant === "frameTicks") {
    const tickMs = Math.max(1, frameTickMsProp ?? 40)
    const durationMs = Math.max(1, rangeEndMs - rangeStartMs)
    const totalFrames = Math.max(1, Math.round(durationMs / tickMs))
    const tickPercents = frameTickPositionsPercent(totalFrames)
    return (
      <div
        ref={containerRef}
        className={cn("relative min-h-0 flex-1 overflow-hidden bg-ui-bg", dimmed ? "opacity-80" : "opacity-100")}
        aria-hidden
      >
        {tickPercents.map((leftPct, i) => (
          <div
            key={`tick-${i}-${leftPct.toFixed(3)}`}
            className="pointer-events-none absolute inset-y-0 w-px dark:bg-ui-bg-elevated/15 light:bg-clay-3/40"
            style={{ left: `${leftPct}%`, transform: "translateX(-50%)" }}
          />
        ))}
        {overlayTopLeft}
        {overlayTopRight}
      </div>
    )
  }

  const hasFrames = frameUrls.length > 0
  const posterReady = posterStatus === "ready" && !!posterUrl

  if (!hasFrames) {
    return (
      <div ref={containerRef} className="relative flex-1 overflow-hidden bg-black">
        {posterReady ? (
          <img
            src={posterUrl!}
            alt=""
            className={cn(
              "h-full w-full object-cover transition-opacity",
              dimmed ? "opacity-70 group-hover:opacity-90" : "opacity-100",
            )}
          />
        ) : posterStatus === "pending" || posterStatus === "processing" ? (
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-ui-link/50" />
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[7px] text-ui-text-secondary">—</div>
        )}
        {overlayTopLeft}
        {overlayTopRight}
      </div>
    )
  }

  const safeAspect = aspectRatio > 0 ? aspectRatio : 16 / 9
  const thumbW = Math.max(1, Math.round(innerHeight * safeAspect))
  const intervalMs = Math.max(1, frameIntervalMs ?? 1000)
  const fittingCount = containerWidth > 0 ? Math.max(1, Math.floor(containerWidth / thumbW)) : 1
  const indices = sampleFilmstripIndices({
    rangeStartMs,
    rangeEndMs,
    intervalMs,
    frameCount: frameUrls.length,
    count: fittingCount,
    segments: filmstripSegments,
  })
  const count = Math.max(1, indices.length)
  const singleSlot = count === 1
  const singleSrc = singleSlot ? (posterReady ? posterUrl! : (frameUrls[indices[0] ?? 0] ?? null)) : null

  const rowClass = cn(
    "flex h-full w-full transition-opacity",
    dimmed ? "opacity-70 group-hover:opacity-90" : "opacity-100",
  )

  const inner =
    singleSlot && singleSrc ? (
      <img src={singleSrc} alt="" className="h-full w-full min-w-0 object-cover" loading="lazy" />
    ) : (
      indices.map((frameIdx, i) => (
        <img
          key={`${frameIdx}-${i}`}
          src={frameUrls[frameIdx]!}
          alt=""
          className="h-full min-w-0 flex-1 object-cover"
          loading="lazy"
        />
      ))
    )

  return (
    <div ref={containerRef} className="relative min-h-0 flex-1 overflow-hidden bg-black">
      <div className={rowClass}>{inner}</div>
      {overlayTopLeft}
      {overlayTopRight}
    </div>
  )
}

export type DraftMarkerLike = { clientId: string; mark_ms: number }

type TimelineMarkersOverlayProps = {
  draftMarkersSorted: DraftMarkerLike[]
  totalDurationMs: number
  frameMs: number
  previewVideoRef: RefObject<HTMLVideoElement | null>
  aspectRatio: number
  markerPreview: { clientId: string; ms: number } | null
  framePreviewMarkerId: string | null
  onPointerEnterMarker: (clientId: string) => void
  onPointerLeaveMarker: (clientId: string, e: React.PointerEvent) => void
  seekToMs: (ms: number) => void
  markerPointerDown: (marker: DraftMarkerLike, e: React.PointerEvent) => void
  nudgeMarkerMs: (clientId: string, dir: -1 | 1) => void
  destroyDraftMarker: (clientId: string) => void
  showSplitAtMarker: boolean
  splitting?: boolean
  onSplitAtMs?: (ms: number, draftClientId?: string) => void
  /** Меньше вертикальный зазор над полосой (студия, компактная панель). */
  compact?: boolean
  /**
   * Панель действий над меткой рендерится в document.body (fixed, top z-index).
   * Нужно поверх overflow-x-auto zoom-скроллера (аниматик) и нативного video (студия).
   */
  portalMarkerToolbar?: boolean
  /** Вызов после закрытия портала с кнопками (таймер hide); для сброса превью кадра. */
  onPortalMarkerChromeDismiss?: () => void
  /** Если задан — клик по метке активирует её вместо seek playhead (аниматик). */
  onMarkerActivate?: (marker: DraftMarkerLike) => void
  /**
   * Позиция в px (как ruler/playhead). Без него — percent от ширины родителя (студия).
   * Нужен на аниматике: fit-zoom расширяет контент, и % расходится с ms*pxPerMs.
   */
  pxPerMs?: number
  /** Активная метка: скрываем короткий stub — остаётся full-height линия снаружи. */
  activeMarkerClientId?: string | null
}

/** Корень портала кнопок метки — для `closest()` при pointerleave колонки метки. */
export const MARKER_TOOLBAR_PORTAL_ATTR = "data-marker-toolbar-portal"

function markerAnchorSelector(clientId: string) {
  return `[data-marker-anchor="${CSS.escape(clientId)}"]`
}

type MarkerChromeProps = {
  m: DraftMarkerLike
  ms: number
  framePreviewMarkerId: string | null
  frameMs: number
  previewVideoRef: RefObject<HTMLVideoElement | null>
  aspectRatio: number
  showSplitAtMarker: boolean
  splitting: boolean
  onSplitAtMs?: (ms: number, draftClientId?: string) => void
  nudgeMarkerMs: (clientId: string, dir: -1 | 1) => void
  destroyDraftMarker: (clientId: string) => void
}

function MarkerToolbarChrome({
  m,
  ms,
  framePreviewMarkerId,
  frameMs,
  previewVideoRef,
  aspectRatio,
  showSplitAtMarker,
  splitting,
  onSplitAtMs,
  nudgeMarkerMs,
  destroyDraftMarker,
}: MarkerChromeProps) {
  return (
    <>
      {framePreviewMarkerId === m.clientId ? (
        <MarkerFramePreview ms={ms} frameMs={frameMs} videoRef={previewVideoRef} aspectRatio={aspectRatio} />
      ) : null}
      <div
        className="relative flex shrink-0 gap-0.5 rounded border border-ui-border p-0.5 shadow-xl dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          title="На кадр влево"
          className="rounded p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(ev) => {
            ev.stopPropagation()
            nudgeMarkerMs(m.clientId, -1)
          }}
        >
          <ChevronLeft className="h-3 w-3" />
        </button>
        {showSplitAtMarker && onSplitAtMs ? (
          <button
            type="button"
            title="Разрезать здесь"
            className="rounded p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-accent-text"
            disabled={splitting}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation()
              void onSplitAtMs(ms, m.clientId)
            }}
          >
            <Scissors className="h-3 w-3" />
          </button>
        ) : null}
        <button
          type="button"
          title="Удалить метку"
          className="rounded p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-status-failed"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(ev) => {
            ev.stopPropagation()
            destroyDraftMarker(m.clientId)
          }}
        >
          <Trash2 className="h-3 w-3" />
        </button>
        <button
          type="button"
          title="На кадр вправо"
          className="rounded p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(ev) => {
            ev.stopPropagation()
            nudgeMarkerMs(m.clientId, 1)
          }}
        >
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </>
  )
}

/** Marker pins above the filmstrip (animatic draft markers + studio server markers as draft-shaped). */
export const TimelineMarkersOverlay = memo(function TimelineMarkersOverlay({
  draftMarkersSorted,
  totalDurationMs,
  frameMs,
  previewVideoRef,
  aspectRatio,
  markerPreview,
  framePreviewMarkerId,
  onPointerEnterMarker,
  onPointerLeaveMarker,
  seekToMs,
  markerPointerDown,
  nudgeMarkerMs,
  destroyDraftMarker,
  showSplitAtMarker,
  splitting = false,
  onSplitAtMs,
  compact = false,
  portalMarkerToolbar = false,
  onPortalMarkerChromeDismiss,
  onMarkerActivate,
  pxPerMs,
  activeMarkerClientId = null,
}: TimelineMarkersOverlayProps) {
  const dur = Math.max(1, totalDurationMs)
  const hideChromeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [portalChrome, setPortalChrome] = useState<{
    clientId: string
    left: number
    top: number
  } | null>(null)

  const cancelHideChromeTimer = () => {
    if (hideChromeTimerRef.current != null) {
      clearTimeout(hideChromeTimerRef.current)
      hideChromeTimerRef.current = null
    }
  }

  const scheduleHidePortalChrome = () => {
    cancelHideChromeTimer()
    hideChromeTimerRef.current = setTimeout(() => {
      setPortalChrome(null)
      hideChromeTimerRef.current = null
      onPortalMarkerChromeDismiss?.()
    }, 160)
  }

  useEffect(() => () => cancelHideChromeTimer(), [])

  useEffect(() => {
    if (!portalChrome) return
    if (!draftMarkersSorted.some((x) => x.clientId === portalChrome.clientId)) {
      setPortalChrome(null)
      onPortalMarkerChromeDismiss?.()
    }
  }, [draftMarkersSorted, portalChrome, onPortalMarkerChromeDismiss])

  const portalChromeClientId = portalChrome?.clientId

  useLayoutEffect(() => {
    if (!portalMarkerToolbar || !portalChromeClientId) return
    const clientId = portalChromeClientId
    const update = () => {
      const el = document.querySelector<HTMLElement>(markerAnchorSelector(clientId))
      if (!el) return
      const r = el.getBoundingClientRect()
      setPortalChrome((prev) =>
        prev && prev.clientId === clientId ? { clientId, left: r.left + r.width / 2, top: r.top } : prev,
      )
    }
    update()
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [portalMarkerToolbar, portalChromeClientId, markerPreview?.ms, markerPreview?.clientId])

  const portalMarker =
    portalMarkerToolbar && portalChrome
      ? draftMarkersSorted.find((x) => x.clientId === portalChrome.clientId)
      : undefined
  const portalMs =
    portalMarker && markerPreview?.clientId === portalMarker.clientId ? markerPreview.ms : portalMarker?.mark_ms

  const portalNode =
    portalMarkerToolbar && portalChrome && portalMarker != null && portalMs != null && typeof document !== "undefined"
      ? createPortal(
          <div
            {...{ [MARKER_TOOLBAR_PORTAL_ATTR]: "" }}
            className="pointer-events-auto fixed z-[9999] flex flex-col items-center gap-0.5"
            style={{
              left: portalChrome.left,
              top: portalChrome.top,
              transform: "translate(-50%, calc(-100% - 6px))",
            }}
            onPointerEnter={cancelHideChromeTimer}
            onPointerLeave={scheduleHidePortalChrome}
          >
            <MarkerToolbarChrome
              m={portalMarker}
              ms={portalMs}
              framePreviewMarkerId={framePreviewMarkerId}
              frameMs={frameMs}
              previewVideoRef={previewVideoRef}
              aspectRatio={aspectRatio}
              showSplitAtMarker={showSplitAtMarker}
              splitting={splitting}
              onSplitAtMs={onSplitAtMs}
              nudgeMarkerMs={nudgeMarkerMs}
              destroyDraftMarker={destroyDraftMarker}
            />
          </div>,
          document.body,
        )
      : null

  return (
    <>
      <div className={cn("relative z-[80]", compact ? "mb-0.5 h-5" : "mb-1 h-7")}>
        {draftMarkersSorted.map((m) => {
          const ms = markerPreview?.clientId === m.clientId ? markerPreview.ms : m.mark_ms
          const isActiveMarker = activeMarkerClientId === m.clientId
          const leftStyle = pxPerMs != null && pxPerMs > 0 ? { left: ms * pxPerMs } : { left: `${(ms / dur) * 100}%` }
          return (
            <div
              key={m.clientId}
              data-marker-anchor={m.clientId}
              className="group/marker pointer-events-auto absolute bottom-0 z-[85] flex min-h-[3.25rem] w-9 -translate-x-1/2 flex-col items-center"
              style={leftStyle}
              onPointerEnter={(e) => {
                onPointerEnterMarker(m.clientId)
                if (portalMarkerToolbar) {
                  cancelHideChromeTimer()
                  const el = e.currentTarget
                  const r = el.getBoundingClientRect()
                  setPortalChrome({ clientId: m.clientId, left: r.left + r.width / 2, top: r.top })
                }
              }}
              onPointerLeave={(e) => {
                onPointerLeaveMarker(m.clientId, e)
                if (portalMarkerToolbar) scheduleHidePortalChrome()
              }}
            >
              {!portalMarkerToolbar ? (
                <div className="relative z-[95] mb-0.5 hidden shrink-0 flex-col items-center gap-0.5 group-hover/marker:flex">
                  <MarkerToolbarChrome
                    m={m}
                    ms={ms}
                    framePreviewMarkerId={framePreviewMarkerId}
                    frameMs={frameMs}
                    previewVideoRef={previewVideoRef}
                    aspectRatio={aspectRatio}
                    showSplitAtMarker={showSplitAtMarker}
                    splitting={splitting}
                    onSplitAtMs={onSplitAtMs}
                    nudgeMarkerMs={nudgeMarkerMs}
                    destroyDraftMarker={destroyDraftMarker}
                  />
                </div>
              ) : null}
              <div className="min-h-1 w-full flex-1 shrink-0" aria-hidden />
              <button
                type="button"
                className="flex shrink-0 cursor-ew-resize flex-col items-center active:cursor-ew-resize"
                onPointerDown={(e) => markerPointerDown(m, e)}
                onClick={(e) => {
                  e.stopPropagation()
                  if (onMarkerActivate) onMarkerActivate({ clientId: m.clientId, mark_ms: ms })
                  else seekToMs(ms)
                }}
              >
                <span className="pointer-events-none rounded bg-[var(--ui-chip-bg-medium)] px-0.5 py-px text-[8px] whitespace-nowrap text-ui-text-secondary tabular-nums">
                  {formatMs(ms)}
                </span>
                {isActiveMarker ? null : (
                  <span className="mt-0.5 h-5 w-0.5 shrink-0 rounded-full bg-amber-500 shadow-sm shadow-amber-900/50" />
                )}
              </button>
            </div>
          )
        })}
      </div>
      {portalNode}
    </>
  )
})
